import * as React from 'react';
import { Formik, Form, Field } from 'formik';

import { SourceCodeEditor } from 'components/common';
import { Button, ControlLabel, HelpBlock } from 'components/bootstrap';
import { isPermitted } from 'util/PermissionsMixin';
import useCurrentUser from 'hooks/useCurrentUser';
import {
  DEFAULT_BODY_TEMPLATE,
  DEFAULT_HTML_BODY_TEMPLATE,
} from 'components/event-notifications/event-notification-types/emailNotificationTemplates';

import useEmailTemplates from './useEmailTemplate';

const DEFAULT_CONFIG = {
  override_defaults: false,
  text_body: DEFAULT_BODY_TEMPLATE,
  html_body: DEFAULT_HTML_BODY_TEMPLATE,
};

const EmailTemplateConfiguration = () => {
  const currentUser = useCurrentUser();
  const { templateConfig, onUpdateEmailTemplates } = useEmailTemplates();

  const updateEmailTemplates = (values): Promise<void> => onUpdateEmailTemplates(values);

  const isDisabled = !isPermitted(currentUser.permissions, 'email_notification_defaults:update');

  const initialValues = {
    override_defaults: templateConfig?.override_defaults ?? DEFAULT_CONFIG.override_defaults,
    text_body: templateConfig?.text_body ?? DEFAULT_CONFIG.text_body,
    html_body: templateConfig?.html_body ?? DEFAULT_CONFIG.html_body,
  };

  return (
    <Formik initialValues={initialValues} onSubmit={updateEmailTemplates} enableReinitialize>
      {({ dirty, handleSubmit, setFieldValue }) => (
        <Form onSubmit={handleSubmit}>
          <Field name="override_defaults">
            {({ field }) => (
              <>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="checkbox"
                    id="override-defaults"
                    name={field.name}
                    checked={!!field.value}
                    onChange={(e) => setFieldValue(field.name, e.target.checked)}
                    disabled={isDisabled}
                  />
                  Use custom templates
                </label>
                <HelpBlock>When enabled, these templates will be used instead of the global defaults.</HelpBlock>
              </>
            )}
          </Field>

          <ControlLabel>Body Template</ControlLabel>
          <Field name="text_body">
            {({ field }) => (
              <>
                <SourceCodeEditor
                  id="body-template"
                  name={field.name}
                  mode="text"
                  theme="light"
                  value={field.value ?? ''}
                  onChange={(val: string) => setFieldValue(field.name, val)}
                />
                <HelpBlock>
                  The template that will be used to generate the email body. You may also&nbsp;
                  <Button
                    bsStyle="link"
                    type="button"
                    onClick={() => {
                      setFieldValue(field.name, DEFAULT_BODY_TEMPLATE);
                    }}>
                    restore the default value
                  </Button>
                  .
                </HelpBlock>
              </>
            )}
          </Field>

          <ControlLabel>HTML Body Template</ControlLabel>
          <Field name="html_body">
            {({ field }) => (
              <>
                <SourceCodeEditor
                  id="html-body-template"
                  name={field.name}
                  mode="text"
                  theme="light"
                  value={field.value ?? ''}
                  onChange={(val: string) => setFieldValue(field.name, val)}
                />
                <HelpBlock>
                  The template that will be used to generate the email HTML body. You may also&nbsp;
                  <Button
                    bsStyle="link"
                    type="button"
                    onClick={() => {
                      setFieldValue(field.name, DEFAULT_HTML_BODY_TEMPLATE);
                    }}>
                    restore the default value
                  </Button>
                  .
                </HelpBlock>
              </>
            )}
          </Field>

          {!isDisabled && (
            <Button bsSize="xsmall" bsStyle="info" type="submit" disabled={!dirty}>
              Update templates
            </Button>
          )}
        </Form>
      )}
    </Formik>
  );
};

export default EmailTemplateConfiguration;
